import { useEffect, useState } from "react";
import axios, { API } from "@/lib/api";
import { Loader2, ArrowUpRight, ArrowDownLeft } from "lucide-react";

const ACTIVITY_LABEL = {
  invite_pre_alloc: "Pre-allocated on invite",
  invite_grant: "Granted to member (on accept)",
  pre_alloc_push: "Pre-allocation top-up (before accept)",
  pre_alloc_recall: "Recalled pre-allocation",
  push: "Pushed credits to member",
  recall: "Recalled credits from member",
};

/**
 * Team activity tab — chronological audit log surfaced from
 * `credit_transfers`. Read-only; renders one row per ledger entry with
 * intent (push/recall/invite/etc), amount, who, and when.
 */
export default function TeamActivityTab({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/workspaces/${workspaceId}/transfers?limit=80`);
        if (!cancelled) setItems(r.data?.items || []);
      } catch (err) {
        console.warn("activity load failed", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (loading) {
    return (
      <div className="max-w-3xl mt-8 py-12 text-center text-sm text-white/45 inline-flex items-center justify-center gap-2 w-full">
        <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]" /> Loading activity…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="max-w-3xl mt-8 rounded-xl border border-white/10 bg-[#0E0E0E] py-14 text-center text-sm text-white/55" data-testid="team-activity-empty">
        No credit movements yet. Push or recall credits and they&apos;ll show up here.
      </div>
    );
  }
  return (
    <div className="max-w-3xl mt-6 rounded-xl border border-white/10 bg-[#0E0E0E] overflow-hidden" data-testid="team-activity-list">
      {items.map((row) => {
        const isPush = row.amount > 0;
        const label = ACTIVITY_LABEL[row.kind] || row.kind;
        const Icon = isPush ? ArrowUpRight : ArrowDownLeft;
        return (
          <div
            key={row.id}
            className="grid grid-cols-12 items-center px-5 py-3 border-b border-white/5 last:border-b-0"
            data-testid={`team-activity-row-${row.id}`}
          >
            <div className="col-span-1">
              <Icon className={`w-4 h-4 ${isPush ? "text-emerald-300" : "text-amber-300"}`} />
            </div>
            <div className="col-span-7">
              <div className="text-sm text-white">{label}</div>
              <div className="text-[11px] text-white/45">
                {row.member_name || row.member_email || "—"}
              </div>
            </div>
            <div className="col-span-2 text-right">
              <span className={`text-sm font-medium ${isPush ? "text-emerald-300" : "text-amber-300"}`}>
                {isPush ? "+" : ""}{row.amount}
              </span>
            </div>
            <div className="col-span-2 text-right text-[11px] text-white/45">
              {new Date(row.at).toLocaleString(undefined, {
                day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
